import { useEffect, useState } from "react";
import { Download, X, Share, PlusSquare, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";

/**
 * Smart PWA install prompt.
 *
 * Strategy:
 *  - Track visits across days in localStorage (`pwa_visit_days`).
 *  - Only show the banner when the user has been engaged: 3+ distinct days
 *    in the last 7. This avoids pestering one-time visitors and waits until
 *    the user is clearly getting value.
 *  - If the user dismisses, snooze for 7 days (`pwa_snooze_until`). They'll
 *    be re-prompted later — power users won't lose access to the suggestion
 *    permanently.
 *  - On Chromium / Android, hook into the native `beforeinstallprompt` so we
 *    can call `prompt()` directly. On iOS Safari (no API), show a small
 *    instructions card explaining the manual flow (Share → Add to Home).
 *  - Hide instantly if running already in standalone (already installed).
 */

const VISIT_DAYS_KEY = "pwa_visit_days";
const SNOOZE_KEY = "pwa_snooze_until";
const REQUIRED_DAYS = 3;
const WINDOW_DAYS = 7;
const SNOOZE_DAYS = 7;

function todayStamp() {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
}

function recordVisit() {
  try {
    const raw = localStorage.getItem(VISIT_DAYS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    const today = todayStamp();
    if (!arr.includes(today)) {
      // Keep only the last 14 entries to avoid bloating storage.
      arr.push(today);
      while (arr.length > 14) arr.shift();
      localStorage.setItem(VISIT_DAYS_KEY, JSON.stringify(arr));
    }
    return arr;
  } catch {
    return [];
  }
}

function isEngagedUser() {
  try {
    const raw = localStorage.getItem(VISIT_DAYS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (arr.length === 0) return false;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - WINDOW_DAYS);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    const recent = arr.filter((d) => d >= cutoffStr);
    return recent.length >= REQUIRED_DAYS;
  } catch {
    return false;
  }
}

function isSnoozed() {
  const until = localStorage.getItem(SNOOZE_KEY);
  if (!until) return false;
  return Number(until) > Date.now();
}

function isStandalone() {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

function isIOSSafari() {
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return iOS && webkit;
}

export default function PWAInstallPrompt() {
  const { t, lang } = useI18n();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState("native"); // "native" | "ios"

  useEffect(() => {
    // 1. Already installed → never show.
    if (isStandalone()) return;
    // 2. Snoozed → wait it out.
    if (isSnoozed()) return;

    // 3. Record today's visit and check engagement gate.
    recordVisit();

    const showWhenReady = () => {
      if (isEngagedUser()) setVisible(true);
    };

    // Chromium / Android: defer the native prompt for engaged users.
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setMode("native");
      showWhenReady();
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", installedHandler);

    // iOS Safari has no API — show manual instructions card to engaged users
    // after a short delay so it doesn't compete with the page load.
    if (isIOSSafari()) {
      setMode("ios");
      const id = setTimeout(showWhenReady, 1500);
      return () => {
        clearTimeout(id);
        window.removeEventListener("beforeinstallprompt", handler);
        window.removeEventListener("appinstalled", installedHandler);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const tr = (ca, es) => (lang === "es" ? es : ca);

  const handleInstall = async () => {
    if (mode === "ios") return; // The card itself is the instruction
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "dismissed") {
      const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(SNOOZE_KEY, String(until));
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(SNOOZE_KEY, String(until));
    setVisible(false);
  };

  if (!visible) return null;

  // iOS Safari variant — manual instructions
  if (mode === "ios") {
    return (
      <div
        data-testid="pwa-install-prompt"
        data-mode="ios"
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[94%] max-w-md
                   rounded-2xl border border-white/10 bg-[#0B1220]/95 backdrop-blur-xl
                   shadow-2xl shadow-blue-900/30 p-4
                   animate-in slide-in-from-bottom-4 duration-300">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">
              {tr("Instal·la HOSYS al teu iPhone",
                  "Instala HOSYS en tu iPhone")}
            </p>
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              {tr(
                <>
                  Toca <Share size={11} className="inline mb-0.5 text-blue-300" /> i després
                  <strong className="mx-1 text-slate-200">"Afegir a Pantalla d'inici"</strong>
                  <PlusSquare size={11} className="inline ml-1 mb-0.5 text-blue-300" />
                </>,
                <>
                  Toca <Share size={11} className="inline mb-0.5 text-blue-300" /> y luego
                  <strong className="mx-1 text-slate-200">"Añadir a Pantalla de inicio"</strong>
                  <PlusSquare size={11} className="inline ml-1 mb-0.5 text-blue-300" />
                </>
              )}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            data-testid="pwa-dismiss-btn"
            className="shrink-0 w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400"
            aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="pwa-install-prompt"
      data-mode="native"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[92%] max-w-md
                 rounded-2xl border border-white/10 bg-[#0B1220]/95 backdrop-blur-xl
                 shadow-2xl shadow-blue-900/30 p-4 flex items-center gap-3
                 animate-in slide-in-from-bottom-4 duration-300">
      <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
        <Download className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white truncate">
          {t("pwa.title", tr("Instal·la HO-SYS", "Instala HO-SYS"))}
        </p>
        <p className="text-xs text-slate-400 truncate">
          {tr(
            "Accés ràpid · funciona sense connexió · sense desconnexions",
            "Acceso rápido · funciona sin conexión · sin desconexiones"
          )}
        </p>
      </div>
      <Button
        size="sm"
        onClick={handleInstall}
        data-testid="pwa-install-btn"
        className="bg-blue-600 hover:bg-blue-700 text-white">
        {t("pwa.install", tr("Instal·lar", "Instalar"))}
      </Button>
      <button
        onClick={handleDismiss}
        data-testid="pwa-dismiss-btn"
        className="shrink-0 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-slate-400"
        aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
